import { makeAutoObservable, runInAction } from "mobx";

function buildFileHash(path: string, line?: number): string {
    const encoded = encodeURIComponent(path);
    return line ? `${encoded}:${line}` : encoded;
}

function parseFileHash(raw: string): { path: string; line: number | null } {
    const colonIdx = raw.lastIndexOf(':');
    if (colonIdx !== -1) {
        const lineNum = parseInt(raw.slice(colonIdx + 1), 10);
        if (!isNaN(lineNum) && lineNum > 0) {
            return { path: decodeURIComponent(raw.slice(0, colonIdx)), line: lineNum };
        }
    }
    return { path: decodeURIComponent(raw), line: null };
}

const HISTORY_MAX = 10;
// Sentinel URL fragment pushed when the agent pane opens on mobile so the browser
// back button can close it without navigating the file history.
const AGENT_HASH = '__agent__';

// Counts hash changes initiated by our own code so the background monitor can
// distinguish them from external navigations (browser back/forward, URL bar).
// Not observable — plain counter, mutated synchronously before the navigation.

export interface ContextRef {
    type: "browser" | "terminal";
    id: string;
    label: string;
}

/**
 * Stores shared UI state that must stay in sync across sections.
 * activeFilePath is always a repo-relative path (the same format git status uses),
 * e.g. "src/components/App.tsx".  CodeSection converts to/from absolute paths
 * internally; ReviewSection uses this format natively.
 */
export class StateStore {
    activeFilePath: string | null = null;
    // Keyed by viewStateKey; holds opaque Monaco view-state blobs (cursor + scroll).
    // Non-observable — mutated imperatively by MonacoEditor, never drives reactions.
    monacoViewStates: Map<string, unknown> = new Map();
    // Keyed by repo-relative file path; holds the last known scroll top for cross-section sync.
    // Non-observable — mutated imperatively, never drives reactions.
    monacoScrollTops: Map<string, number> = new Map();
    contextFilePaths: string[] = [];
    contextRefs: ContextRef[] = [];
    openInCodeRequest: { path: string } | null = null;
    openInReviewRequest: { path: string } | null = null;
    sectionRequest: { section: "code" | "review" | "deploy" } | null = null;
    // Repo actions from BranchSelect's menu; handled by ReviewSectionStore (owns the banners).
    repoActionRequest: { action: "push" | "pull" | "reset" | "refresh" } | null = null;
    // In-flight repo-level operations; BranchSelect swaps its actions button for a preloader.
    repoActionsInFlight: number = 0;
    isSearchOpen: boolean = false;
    currentSection: "code" | "review" | "deploy" = "code";
    mobileAccordionIndex: number = 0;
    vaultMode: boolean = false;

    private fileHistory: Array<{ path: string | null; line: number | null }> = [];
    private historyIndex: number = -1;
    pendingLine: number | null = null;
    // Incremented each time the URL hash changes due to external navigation (browser
    // back/forward, address-bar edit). AppViewStore reacts to this to switch sections.
    fileUrlNavigated: number = 0;
    // Plain (non-observable) fields; excluded from makeAutoObservable below.
    _ownHashChangePending = 0;
    _agentHistoryPushed = false;

    constructor() {
        makeAutoObservable(this, { monacoViewStates: false, monacoScrollTops: false, _ownHashChangePending: false, _agentHistoryPushed: false });

        // Restore last open file (and optional line anchor) from the URL hash on page reload.
        const initialHash = window.location.hash;
        if (initialHash.startsWith('#') && initialHash.length > 1) {
            const rawPath = initialHash.slice(1);
            if (rawPath === AGENT_HASH) {
                // Stale agent sentinel from a prior session — remove it.
                history.replaceState({}, '', window.location.pathname + window.location.search);
            } else {
                const { path, line } = parseFileHash(rawPath);
                this.activeFilePath = path;
                this.pendingLine = line;
                this.fileHistory = [{ path, line }];
                this.historyIndex = 0;
            }
        }

        // Background monitor: every URL fragment change — whether triggered by our own
        // setActiveFilePath, the browser Back/Forward buttons, or the user typing a hash
        // directly in the address bar — flows through here and updates the rolling
        // last-10 file history. setActiveFilePath/goBack/goForward eagerly set
        // activeFilePath for immediate reactivity; the monitor owns fileHistory and
        // historyIndex bookkeeping.
        window.addEventListener('hashchange', (event) => {
            const raw = window.location.hash;

            // Consume one pending "own change" token BEFORE any early returns so the
            // counter never drifts when navigating to the empty-hash (no-file) state.
            const isOwnChange = this._ownHashChangePending > 0;
            if (isOwnChange) this._ownHashChangePending--;

            // When navigating AWAY from the agent sentinel, clear the flag so that a
            // later programmatic popAgentHistoryEntry() call becomes a no-op and doesn't
            // call history.back() a second time.
            try {
                if (new URL(event.oldURL).hash === '#' + AGENT_HASH) {
                    this._agentHistoryPushed = false;
                }
            } catch (_) {}

            // Empty hash = navigated to the "no file open" state (forward to the entry
            // pushed when the last tab was closed, or goBack() targeting a null entry).
            if (!raw || raw.length <= 1) {
                if (!isOwnChange) {
                    runInAction(() => {
                        this.activeFilePath = null;
                        this.pendingLine = null;
                        for (let i = this.fileHistory.length - 1; i >= 0; i--) {
                            if (this.fileHistory[i].path === null) { this.historyIndex = i; break; }
                        }
                        this.fileUrlNavigated++;
                    });
                }
                return;
            }

            // The agent sentinel is not a real file path — handle it and bail out.
            if (raw === '#' + AGENT_HASH) {
                if (!isOwnChange) {
                    // External navigation arrived at the sentinel (e.g. browser forward button
                    // after the agent was already closed). Replace it with the current file so
                    // the sentinel is silently removed and the agent is never re-opened.
                    const cur = this.fileHistory[this.historyIndex];
                    if (cur && cur.path !== null) {
                        history.replaceState({}, '', '#' + buildFileHash(cur.path, cur.line ?? undefined));
                    } else {
                        history.replaceState({}, '', window.location.pathname + window.location.search);
                    }
                }
                return;
            }

            const { path, line } = parseFileHash(raw.slice(1));

            const currentEntry = this.fileHistory[this.historyIndex];
            if (currentEntry?.path === path) {
                // goBack/goForward eagerly update historyIndex before calling
                // history.back()/forward(), so by the time hashchange fires the entry
                // at historyIndex already matches. Just sync the line if it changed.
                if (currentEntry.line !== line) {
                    runInAction(() => {
                        this.pendingLine = line;
                        this.fileHistory[this.historyIndex] = { path, line };
                    });
                }
                if (!isOwnChange) runInAction(() => { this.fileUrlNavigated++; });
                return;
            }

            // For external navigations (browser back/forward, address-bar edit): check
            // whether the target path already exists in our history buffer. If it does,
            // this is a back/forward rather than a fresh navigation — update position
            // without pushing a new entry or dropping forward history.
            if (!isOwnChange) {
                let existingIdx = -1;
                for (let i = this.fileHistory.length - 1; i >= 0; i--) {
                    if (this.fileHistory[i].path === path) { existingIdx = i; break; }
                }
                if (existingIdx !== -1) {
                    runInAction(() => {
                        this.historyIndex = existingIdx;
                        this.activeFilePath = path;
                        this.pendingLine = line ?? this.fileHistory[existingIdx].line;
                        this.fileUrlNavigated++;
                    });
                    return;
                }
            }

            // New file: drop any forward entries, push, cap buffer to HISTORY_MAX.
            runInAction(() => {
                this.activeFilePath = path;
                this.pendingLine = line;
                const head = this.fileHistory.slice(0, this.historyIndex + 1);
                head.push({ path, line });
                this.fileHistory = head.length > HISTORY_MAX ? head.slice(-HISTORY_MAX) : head;
                this.historyIndex = this.fileHistory.length - 1;
                if (!isOwnChange) this.fileUrlNavigated++;
            });
        });
    }

    get canGoBack(): boolean {
        return this.historyIndex > 0;
    }

    get canGoForward(): boolean {
        return this.historyIndex < this.fileHistory.length - 1;
    }

    setActiveFilePath(path: string | null, line?: number) {
        // Eager update for immediate reactivity in CodeSection and other observers.
        this.activeFilePath = path;
        this.pendingLine = line ?? null;
        if (path === null) {
            // Push a null entry to internal history and clear the browser hash so that
            // pressing back from the "no file open" state restores the previous file.
            // pushState doesn't fire hashchange so we update fileHistory directly.
            if (this.fileHistory[this.historyIndex]?.path !== null) {
                const head = this.fileHistory.slice(0, this.historyIndex + 1);
                head.push({ path: null, line: null });
                this.fileHistory = head.length > HISTORY_MAX ? head.slice(-HISTORY_MAX) : head;
                this.historyIndex = this.fileHistory.length - 1;
                history.pushState({}, '', window.location.pathname + window.location.search);
            }
            return;
        }
        const newHash = buildFileHash(path, line);
        if (window.location.hash !== '#' + newHash) {
            if (this._agentHistoryPushed) {
                // The agent sentinel is the current top-of-stack entry. Replace it in-place
                // rather than pushing a new entry on top, so the sentinel never ends up in
                // the middle of browser history where a back-navigation would land on it.
                // replaceState doesn't fire hashchange, so we update fileHistory directly.
                this._agentHistoryPushed = false;
                history.replaceState({}, '', '#' + newHash);
                const head = this.fileHistory.slice(0, this.historyIndex + 1);
                head.push({ path, line: line ?? null });
                this.fileHistory = head.length > HISTORY_MAX ? head.slice(-HISTORY_MAX) : head;
                this.historyIndex = this.fileHistory.length - 1;
            } else {
                this._ownHashChangePending++;
                window.location.hash = newHash;
            }
        }
        if (this.contextFilePaths.length <= 1) {
            if (this.contextFilePaths.length === 1 && this.contextFilePaths[0] !== path) {
                this.removeContextFile(this.contextFilePaths[0]);
            }
            this.addContextFile(path);
        }
    }

    goBack() {
        if (!this.canGoBack) return;
        this.historyIndex -= 1;
        const { path, line } = this.fileHistory[this.historyIndex];
        this.activeFilePath = path;
        this.pendingLine = line;
        // history.back() fires hashchange asynchronously; the monitor sees
        // _ownHashChangePending > 0 and treats it as an internal navigation.
        this._ownHashChangePending++;
        history.back();
    }

    goForward() {
        if (!this.canGoForward) return;
        this.historyIndex += 1;
        const { path, line } = this.fileHistory[this.historyIndex];
        this.activeFilePath = path;
        this.pendingLine = line;
        this._ownHashChangePending++;
        history.forward();
    }

    requestOpenInCode(path: string) {
        this.openInCodeRequest = { path };
    }

    requestOpenInReview(path: string) {
        this.openInReviewRequest = { path };
    }

    requestSection(section: "code" | "review" | "deploy") {
        this.sectionRequest = { section };
    }

    requestRepoAction(action: "push" | "pull" | "reset" | "refresh") {
        this.repoActionRequest = { action };
    }

    get isRepoActionInProgress(): boolean {
        return this.repoActionsInFlight > 0;
    }

    async trackRepoAction<T>(op: () => Promise<T>): Promise<T> {
        runInAction(() => { this.repoActionsInFlight++; });
        try {
            return await op();
        } finally {
            runInAction(() => { this.repoActionsInFlight--; });
        }
    }

    addContextFile(path: string) {
        if (!this.contextFilePaths.includes(path)) {
            this.contextFilePaths = [...this.contextFilePaths, path];
        }
    }

    removeContextFile(path: string) {
        this.contextFilePaths = this.contextFilePaths.filter(p => p !== path);
    }

    toggleContextFile(path: string) {
        if (this.contextFilePaths.includes(path)) {
            this.removeContextFile(path);
        } else {
            this.addContextFile(path);
        }
    }

    isContextFile(path: string): boolean {
        return this.contextFilePaths.includes(path);
    }

    addContextRef(ref: ContextRef) {
        if (!this.contextRefs.some(r => r.id === ref.id)) {
            this.contextRefs = [...this.contextRefs, ref];
        }
    }

    removeContextRef(id: string) {
        this.contextRefs = this.contextRefs.filter(r => r.id !== id);
    }

    toggleContextRef(ref: ContextRef) {
        if (this.isContextRef(ref.id)) {
            this.removeContextRef(ref.id);
        } else {
            this.addContextRef(ref);
        }
    }

    isContextRef(id: string): boolean {
        return this.contextRefs.some(r => r.id === id);
    }

    setCurrentSection(section: "code" | "review" | "deploy") {
        this.currentSection = section;
    }

    openSearch() { this.isSearchOpen = true; }
    closeSearch() { this.isSearchOpen = false; }

    // Called by AppViewStore when the agent pane opens on mobile.
    // Pushes the agent sentinel hash so the browser back button can close the pane.
    pushAgentHistoryEntry() {
        if (this._agentHistoryPushed) return;
        this._agentHistoryPushed = true;
        this._ownHashChangePending++;
        window.location.hash = AGENT_HASH;
    }

    // Called by AppViewStore when the agent pane closes on mobile without using the
    // browser back button (swipe, section change, etc.). Navigates back past the
    // sentinel entry so it isn't left dangling in history.
    popAgentHistoryEntry() {
        if (!this._agentHistoryPushed) return;
        this._agentHistoryPushed = false;
        this._ownHashChangePending++;
        history.back();
    }
}
